import { useState, useMemo } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Search, Calendar, Gauge } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { format } from "date-fns";
import { Run } from "@/hooks/useRuns";
import { ROUTES } from "@/lib/routes";

interface RunsListDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  runs: Run[];
}

export function RunsListDialog({
  open,
  onOpenChange,
  runs,
}: RunsListDialogProps) {
  const [searchQuery, setSearchQuery] = useState("");
  const [equipmentFilter, setEquipmentFilter] = useState<string>("all");
  const [displayCount, setDisplayCount] = useState(20);
  const navigate = useNavigate();

  const filteredRuns = useMemo(() => {
    return runs.filter((run) => {
      const matchesSearch = (run.title || "Untitled Run")
        .toLowerCase()
        .includes(searchQuery.toLowerCase());
      const matchesEquipment =
        equipmentFilter === "all" || run.equipment_type === equipmentFilter;
      return matchesSearch && matchesEquipment;
    });
  }, [runs, searchQuery, equipmentFilter]);

  const displayedRuns = filteredRuns.slice(0, displayCount);
  const hasMore = displayCount < filteredRuns.length;

  const handleRunClick = (runId: string) => {
    onOpenChange(false);
    navigate(ROUTES.run(runId));
  };

  const equipmentTypes = Array.from(
    new Set(runs.map((r) => r.equipment_type).filter(Boolean))
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[80vh]">
        <DialogHeader>
          <DialogTitle>Your Runs</DialogTitle>
          <DialogDescription>
            Browse and filter your logged adventures
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {/* Filters */}
          <div className="flex gap-2">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search runs..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-9"
              />
            </div>
            <Select value={equipmentFilter} onValueChange={setEquipmentFilter}>
              <SelectTrigger className="w-[180px]">
                <SelectValue placeholder="Equipment type" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Equipment</SelectItem>
                {equipmentTypes.map((type) => (
                  <SelectItem key={type} value={type}>
                    {type}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Runs List */}
          <ScrollArea className="h-[400px] pr-4">
            {displayedRuns.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                {searchQuery || equipmentFilter !== "all"
                  ? "No runs found matching your filters"
                  : "No runs logged yet"}
              </div>
            ) : (
              <div className="space-y-3">
                {displayedRuns.map((run) => (
                  <div
                    key={run.id}
                    onClick={() => handleRunClick(run.id)}
                    className="p-4 border rounded-lg hover:bg-accent cursor-pointer transition-colors animate-fade-in"
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <h4 className="font-semibold text-sm mb-2">
                          {run.title || "Untitled Run"}
                        </h4>
                        <div className="flex items-center gap-3 text-xs text-muted-foreground">
                          <div className="flex items-center gap-1">
                            <Calendar className="h-3 w-3" />
                            {format(new Date(run.start_time || run.created_at), "MMM d, yyyy")}
                          </div>
                          <div className="flex items-center gap-1">
                            <Gauge className="h-3 w-3" />
                            {run.distance_miles ? run.distance_miles.toFixed(1) : '0.0'} mi
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        {run.photo_urls && run.photo_urls.length > 0 && (
                          <Badge variant="secondary" className="text-xs">
                            📷 {run.photo_urls.length}
                          </Badge>
                        )}
                        {run.equipment_type && (
                          <Badge variant="outline" className="text-xs">
                            {run.equipment_type}
                          </Badge>
                        )}
                      </div>
                    </div>
                  </div>
                ))}

                {/* Load More Button */}
                {hasMore && (
                  <div className="pt-4 flex justify-center">
                    <Button
                      variant="outline"
                      onClick={() => setDisplayCount((prev) => prev + 20)}
                    >
                      Load More ({filteredRuns.length - displayCount} remaining)
                    </Button>
                  </div>
                )}
              </div>
            )}
          </ScrollArea>
        </div>
      </DialogContent>
    </Dialog>
  );
}
